import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/state/appState';
import { CreateJobState } from '../../store/state/createJobState';
import CreateJobGatherInfoStep from './CreateJobGatherInfoStep';
import CreateJobLogFilesStep from './CreateJobLogFilesStep';
import CreateJobMapLogStep from './CreateJobMapLogStep';
import CreateJobUploading from './CreateJobUploading';
import { Prompt, useParams } from 'react-router-dom';
import PageLoadingSpinner from '../PageLoadingSpinner';
import { AppDispatch } from '../../store/actions/appDispatch';
import { initializeCreateJob } from '../../store/actions/createJobActions';
import { CustomFieldValueSummary } from '../../models/dto/customField';
import api from '../../helpers/api';
import { PagedResult } from '../../models/dto/pagedResult';
import { PageIndexOptions } from '../../models/dto/pageIndexOptions';

export default function CreateJobPage(props: { engagementId: number; }) {
	const engagementId = props.engagementId;
	const createJobState = useSelector<AppState, CreateJobState | undefined>(s => s.createJobsState.byEngagement.get(engagementId));
	const dispatch = useDispatch<AppDispatch>();
	const [customFieldsState, setCustomFieldsState] = useState<CustomFieldsState>({ engagementId, customFields: null });
	
	// load custom fields
	useEffect(() => {
		setCustomFieldsState({ engagementId, customFields: null });
		
		const params: PageIndexOptions = { pageIndex: 1, pageSize: 10000 };
		api.get<PagedResult<CustomFieldValueSummary>>(`/api/engagements/${engagementId}/custom-fields`, { params }).then(response => {
			setCustomFieldsState(prev => {
				// handle race conditions
				if (prev.engagementId === engagementId) {
					return { engagementId, customFields: response.data.items ?? [] };
				} else {
					return prev;
				}
			});
		});
	}, [engagementId]);
	
	// initialize 'create job' state
	useEffect(() => {
		if (!createJobState && customFieldsState.customFields) {
			dispatch(initializeCreateJob(engagementId, customFieldsState.customFields));
		}
	}, [engagementId, customFieldsState.customFields]);

	if (!createJobState) {
		return <PageLoadingSpinner />;
	}
	
	if (createJobState.createRequestStatus !== 'none') {
		return <CreateJobUploading createJobState={createJobState} />;
	}
	
	const step = createJobState.currentStep;
	return (
		<React.Fragment>
			{step.type === 'info' && <CreateJobGatherInfoStep step={step} engagementId={engagementId} /> }
			{step.type === 'logFiles' && <CreateJobLogFilesStep step={step} engagementId={engagementId} /> }
			{step.type === 'mapCallFields' &&
				<React.Fragment>
					<CreateJobMapLogStep step={step}
										 callLogData={createJobState.callLogData}
										 engagementId={engagementId}
										 isJobBeingCreated={createJobState.createRequestStatus !== 'none'} />
				</React.Fragment>
			}
			<Prompt message="Are you sure you want to leave? Your changes will be lost." />
		</React.Fragment>
	);
}

interface CustomFieldsState {
    engagementId: number;
    customFields: CustomFieldValueSummary[] | null;
}